import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, eachDayOfInterval, isWeekend, parseISO } from 'date-fns';
import { CalendarRange, Calendar, Clock, Phone, Loader2, FolderOpen, AlertCircle, Trash2, Timer } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { DurationInput } from './shared/duration-input';
import { formatDuration, hoursToMinutes, minutesToHours } from '../lib/timesheet-hours';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { BottomSheet } from './shared/bottom-sheet';
import { useMobile } from '../hooks/use-mobile';
import { useNationalHolidays } from '../contexts/NationalHolidaysContext';
import { useAuth } from '../contexts/AuthContext';
import { orderAssignmentService, OrderAssignment } from '../services/orderAssignmentService';

interface DateRangeEntryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (entries: TimesheetEntryData[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  initialDate?: string;
  initialOrderId?: number;
  initialEntry?: {
    workedHours: number;
    onCall: boolean;
    onCallHours: number;
    overtimeHours: number;
    workPermit: number;
  };
  entryState?: string;
  mode?: 'add' | 'edit';
  /** Ordinary hours already logged by other entries on the same day */
  otherEntriesWorkedHours?: number;
  /** Order IDs already used on this day (excluded from dropdown in add mode) */
  usedOrderIds?: number[];
  /** Approved leave/permit hours on the selected day */
  approvedLeaveHours?: number;
}

export interface TimesheetEntryData {
  date: string;
  orderId?: number;
  /** Ordinary hours; overtime is entered apart */
  workedHours: number;
  /** On call that day, even with 0 hours of work */
  onCall: boolean;
  /** Hours worked while on call (0 when not on call) */
  onCallHours: number;
  overtimeHours: number;
  workPermit: number;
}

const STANDARD_DAILY_HOURS = 8;

export const DateRangeEntryDialog: React.FC<DateRangeEntryDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  initialDate,
  initialOrderId,
  initialEntry,
  entryState,
  mode = 'add',
  otherEntriesWorkedHours = 0,
  usedOrderIds = [],
  approvedLeaveHours = 0,
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { isBlockingHoliday } = useNationalHolidays();
  const { user } = useAuth();

  const dailyWorkedLimit = Math.max(0, STANDARD_DAILY_HOURS - approvedLeaveHours);
  const isWorkedLockedByFullLeave = dailyWorkedLimit <= 0;
  // Ordinary hours still free on the selected day, net of leave and of the other orders
  const remainingOrdinaryHours = minutesToHours(
    Math.max(0, hoursToMinutes(dailyWorkedLimit) - hoursToMinutes(Math.max(0, otherEntriesWorkedHours))),
  );

  const getInitialWorkedHours = () => {
    if (mode === 'edit' && initialEntry) {
      return Number(initialEntry.workedHours || 0);
    }
    return remainingOrdinaryHours;
  };

  const [isRange, setIsRange] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [startDate, setStartDate] = useState(initialDate || format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(initialDate || format(new Date(), 'yyyy-MM-dd'));
  const [workedHours, setWorkedHours] = useState(getInitialWorkedHours);
  const [hasOvertime, setHasOvertime] = useState(Number(initialEntry?.overtimeHours || 0) > 0);
  const [overtimeHours, setOvertimeHours] = useState(Number(initialEntry?.overtimeHours || 0));
  const [onCall, setOnCall] = useState(Boolean(initialEntry?.onCall));
  const [onCallHours, setOnCallHours] = useState(Number(initialEntry?.onCallHours || 0));
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [skipHolidays, setSkipHolidays] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>(initialOrderId?.toString() || '');
  const [orderAssignments, setOrderAssignments] = useState<OrderAssignment[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const isAssignmentValidForDate = (assignment: OrderAssignment, date: Date) => {
    const assignmentStart = parseISO(assignment.startDate);
    const assignmentEnd = assignment.endDate ? parseISO(assignment.endDate) : new Date('9999-12-31');
    const target = new Date(date);
    assignmentStart.setHours(0, 0, 0, 0);
    assignmentEnd.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    return target >= assignmentStart && target <= assignmentEnd;
  };

  // Fetch user's order assignments when dialog opens
  useEffect(() => {
    if (open && user?.id) {
      setLoadingOrders(true);
      orderAssignmentService
        .getByUser(user.id)
        .then((assignments) => {
          // Filter to only active assignments
          const activeAssignments = assignments.filter((a) => a.isActive);
          setOrderAssignments(activeAssignments);
          // Auto-select if only one order
          if (activeAssignments.length === 1) {
            setSelectedOrderId(activeAssignments[0].orderId.toString());
          }
        })
        .catch((err) => {
          console.error('Failed to fetch order assignments:', err);
          setOrderAssignments([]);
        })
        .finally(() => setLoadingOrders(false));
    }
  }, [open, user?.id]);

  // Calculate dates to be filled
  const datesToFill = useMemo(() => {
    if (!isRange) {
      return [parseISO(startDate)];
    }

    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      if (start > end) return [];

      return eachDayOfInterval({ start, end }).filter((date) => {
        if (skipWeekends && isWeekend(date)) return false;
        if (skipHolidays && isBlockingHoliday(date)) return false;
        return true;
      });
    } catch {
      return [];
    }
  }, [isRange, startDate, endDate, skipWeekends, skipHolidays, isBlockingHoliday]);

  const availableOrderAssignments = useMemo(() => {
    if (mode === 'edit') {
      return orderAssignments;
    }

    return orderAssignments.filter((assignment) => {
      if (usedOrderIds.includes(assignment.orderId)) {
        return false;
      }
      return datesToFill.every((date) => isAssignmentValidForDate(assignment, date));
    });
  }, [orderAssignments, mode, usedOrderIds, datesToFill]);

  useEffect(() => {
    if (!open || mode === 'edit') return;

    if (availableOrderAssignments.length === 0) {
      setSelectedOrderId('');
      return;
    }

    const isCurrentSelectionValid = availableOrderAssignments.some(
      (assignment) => assignment.orderId.toString() === selectedOrderId,
    );

    if (!selectedOrderId || !isCurrentSelectionValid) {
      setSelectedOrderId(availableOrderAssignments[0].orderId.toString());
    }
  }, [open, mode, availableOrderAssignments, selectedOrderId]);

  const effectiveOvertimeHours = hasOvertime ? overtimeHours : 0;
  const effectiveOnCallHours = onCall ? onCallHours : 0;

  const handleSubmit = async () => {
    if (datesToFill.length === 0 || !selectedOrderId || !canSubmitHours) return;

    setSaving(true);
    try {
      const entries: TimesheetEntryData[] = datesToFill.map((date) => ({
        date: format(date, 'yyyy-MM-dd'),
        orderId: selectedOrderId ? parseInt(selectedOrderId, 10) : undefined,
        workedHours,
        onCall,
        onCallHours: effectiveOnCallHours,
        overtimeHours: effectiveOvertimeHours,
        workPermit: mode === 'edit' ? (initialEntry?.workPermit ?? 0) : 0,
      }));
      await onSubmit(entries);
      handleClose();
    } finally {
      setSaving(false);
    }
  };

  // Reset values when dialog opens
  useEffect(() => {
    if (open) {
      if (initialDate) {
        setStartDate(initialDate);
        setEndDate(initialDate);
      }
      if (initialOrderId) {
        setSelectedOrderId(initialOrderId.toString());
      }
      if (mode === 'edit' && initialEntry) {
        const initialOvertime = Number(initialEntry.overtimeHours || 0);
        setWorkedHours(getInitialWorkedHours());
        setHasOvertime(initialOvertime > 0);
        setOvertimeHours(initialOvertime);
        setOnCall(Boolean(initialEntry.onCall));
        setOnCallHours(Number(initialEntry.onCallHours || 0));
      } else {
        // Default for new entries: remaining ordinary hours for the day, nothing else.
        setWorkedHours(getInitialWorkedHours());
        setHasOvertime(false);
        setOvertimeHours(0);
        setOnCall(false);
        setOnCallHours(0);
      }
      // In edit mode, disable range selection
      if (mode === 'edit') {
        setIsRange(false);
      }
    }
  }, [open, initialDate, initialOrderId, initialEntry, mode]);

  const handleClose = () => {
    setIsRange(false);
    setWorkedHours(STANDARD_DAILY_HOURS);
    setHasOvertime(false);
    setOvertimeHours(0);
    setOnCall(false);
    setOnCallHours(0);
    setSelectedOrderId('');
    onClose();
  };

  // A range spans days with different leave and other orders: there the page moves each day's excess
  // to overtime on its own, so only the single-day form checks against what is left of that day.
  const ordinaryLimitHours = isRange ? STANDARD_DAILY_HOURS : remainingOrdinaryHours;
  const ordinaryExcessHours = minutesToHours(Math.max(0, hoursToMinutes(workedHours) - hoursToMinutes(ordinaryLimitHours)));
  const overtimeMissing = hasOvertime && hoursToMinutes(overtimeHours) === 0;
  const nothingToLog = workedHours <= 0 && effectiveOvertimeHours <= 0 && !onCall;
  const canSubmitHours = ordinaryExcessHours === 0 && !overtimeMissing && !nothingToLog;
  const entryTotalHours = minutesToHours(
    hoursToMinutes(workedHours) + hoursToMinutes(effectiveOvertimeHours) + hoursToMinutes(effectiveOnCallHours),
  );

  const moveExcessToOvertime = () => {
    setWorkedHours(ordinaryLimitHours);
    setHasOvertime(true);
    setOvertimeHours(minutesToHours(hoursToMinutes(effectiveOvertimeHours) + hoursToMinutes(ordinaryExcessHours)));
  };

  const dialogTitle = mode === 'edit'
    ? t('timesheet.editEntry', 'Edit Entry')
    : t('timesheet.addEntry', 'Add Entry');
  const dialogDescription = mode === 'edit'
    ? t('timesheet.editEntryDescription', 'Modify the hours for this entry')
    : isRange
      ? t('timesheet.addEntryRangeDescription', 'Add entries for multiple days with the same hours')
      : t('timesheet.addEntrySingleDescription', 'Add a single day entry');

  useEffect(() => {
    if (!open) return;

    if (isWorkedLockedByFullLeave && workedHours !== 0) {
      setWorkedHours(0);
    }
  }, [open, isWorkedLockedByFullLeave, workedHours]);

  const formBody = (
    <div className="space-y-4 py-1 sm:py-4">
          {/* In Approval banner */}
          {entryState === 'in_approval' && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{t('timesheet.inApprovalBanner', 'In Approval — changes will be automatically re-submitted')}</span>
            </div>
          )}

          {/* Range Toggle - only show in add mode */}
          {mode !== 'edit' && (
            <div className="flex items-center justify-between">
              <Label htmlFor="range-mode" className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4" />
                {t('timesheet.dateRange', 'Date Range')}
              </Label>
              <Switch
                id="range-mode"
                checked={isRange}
                onCheckedChange={setIsRange}
              />
            </div>
          )}

          {/* Date Selection */}
          <div className={cn('grid gap-4', isRange ? 'grid-cols-2' : 'grid-cols-1')}>
            <div className="space-y-2">
              <Label htmlFor="start-date">
                {isRange ? t('timesheet.startDate', 'Start Date') : t('timesheet.date', 'Date')}
              </Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                disabled={mode === 'edit'}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (!isRange) setEndDate(e.target.value);
                }}
              />
            </div>
            {isRange && (
              <div className="space-y-2">
                <Label htmlFor="end-date">{t('timesheet.endDate', 'End Date')}</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Order Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <FolderOpen className="h-4 w-4" />
              {t('timesheet.order', 'Order')}
            </Label>
            <Select
              value={selectedOrderId}
              onValueChange={setSelectedOrderId}
              disabled={loadingOrders || mode === 'edit'}
            >
              <SelectTrigger>
                <SelectValue placeholder={loadingOrders ? t('common.loading', 'Loading...') : t('timesheet.selectOrder', 'Select an order')} />
              </SelectTrigger>
              <SelectContent>
                {availableOrderAssignments.map((assignment) => (
                    <SelectItem key={assignment.id} value={assignment.orderId.toString()}>
                      {assignment.order?.code} - {assignment.order?.description}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {!loadingOrders && availableOrderAssignments.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t('timesheet.noOrdersAvailableForDate', 'No orders available for the selected date/range')}
              </p>
            )}
          </div>

          {/* Range Options */}
          {isRange && (
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipWeekends}
                  onChange={(e) => setSkipWeekends(e.target.checked)}
                  className="rounded"
                />
                {t('timesheet.skipWeekends', 'Skip weekends')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipHolidays}
                  onChange={(e) => setSkipHolidays(e.target.checked)}
                  className="rounded"
                />
                {t('timesheet.skipHolidays', 'Skip holidays')}
              </label>
            </div>
          )}

          {/* Summary Badge */}
          {isRange && datesToFill.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                {datesToFill.length} {t('timesheet.daysToFill', 'days to fill')}
              </Badge>
              <span className="text-sm text-muted-foreground">
                ({formatDuration(entryTotalHours * datesToFill.length)} h {t('timesheet.total', 'total')})
              </span>
            </div>
          )}

          {/* Ordinary hours */}
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="entry-worked-hours" className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-orange-500" />
                {t('timesheet.entry.ordinaryHours', 'Ordinary hours')}
              </Label>
              {!isRange && !isWorkedLockedByFullLeave && (
                <span className="text-xs text-muted-foreground">
                  {t('timesheet.entry.ordinaryAvailable', '{{hours}} available on this day', { hours: formatDuration(remainingOrdinaryHours) })}
                </span>
              )}
            </div>
            <DurationInput
              id="entry-worked-hours"
              label={t('timesheet.entry.ordinaryHours', 'Ordinary hours')}
              value={workedHours}
              onChange={setWorkedHours}
              disabled={isWorkedLockedByFullLeave}
              invalid={ordinaryExcessHours > 0}
              describedBy={ordinaryExcessHours > 0 ? 'entry-worked-error' : undefined}
            />
            {ordinaryExcessHours > 0 && (
              <div
                id="entry-worked-error"
                className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300 sm:flex-row sm:items-center sm:justify-between"
              >
                <span className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  {t('timesheet.entry.ordinaryExceeded', 'At most {{max}} ordinary hours on this day: record the rest as overtime.', {
                    max: formatDuration(ordinaryLimitHours),
                  })}
                </span>
                <Button type="button" size="sm" variant="outline" className="shrink-0 bg-background" onClick={moveExcessToOvertime}>
                  {t('timesheet.entry.moveToOvertime', 'Move {{hours}} to overtime', { hours: formatDuration(ordinaryExcessHours) })}
                </Button>
              </div>
            )}
          </div>

          {isWorkedLockedByFullLeave && (
            <div className="flex items-center gap-2 rounded-md bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 px-3 py-2 text-sm text-blue-700 dark:text-blue-300">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                {t(
                  'timesheet.errors.fullDayLeaveWorkedLocked',
                  'This day already has 8h of approved leave/permit: ordinary hours stay at 0, you can still record overtime and on-call.',
                )}
              </span>
            </div>
          )}

          {/* Overtime */}
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="entry-has-overtime" className="flex items-center gap-1.5">
                <Timer className="h-4 w-4 text-purple-500" />
                {t('timesheet.entry.overtime', 'Overtime')}
              </Label>
              <Switch
                id="entry-has-overtime"
                checked={hasOvertime}
                onCheckedChange={(checked) => {
                  setHasOvertime(checked);
                  if (!checked) setOvertimeHours(0);
                }}
              />
            </div>
            {hasOvertime && (
              <div className="space-y-1.5">
                <Label htmlFor="entry-overtime-hours" className="text-xs text-muted-foreground">
                  {t('timesheet.entry.overtimeDuration', 'Overtime worked')}
                </Label>
                <DurationInput
                  id="entry-overtime-hours"
                  label={t('timesheet.entry.overtime', 'Overtime')}
                  value={overtimeHours}
                  onChange={setOvertimeHours}
                  invalid={overtimeMissing}
                  describedBy={overtimeMissing ? 'entry-overtime-error' : undefined}
                />
                {overtimeMissing && (
                  <p id="entry-overtime-error" className="text-xs text-red-600 dark:text-red-400">
                    {t('timesheet.entry.overtimeMissing', 'Enter how much overtime you worked, or turn it off.')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* On call */}
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-0.5">
                <Label htmlFor="entry-on-call" className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-blue-500" />
                  {t('timesheet.entry.onCall', 'On call')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('timesheet.entry.onCallQuestion', 'Were you on call on this day?')}
                </p>
              </div>
              <Switch
                id="entry-on-call"
                checked={onCall}
                onCheckedChange={(checked) => {
                  setOnCall(checked);
                  if (!checked) setOnCallHours(0);
                }}
              />
            </div>
            {onCall && (
              <div className="space-y-1.5">
                <Label htmlFor="entry-on-call-hours" className="text-xs text-muted-foreground">
                  {t('timesheet.entry.onCallHours', 'Hours worked while on call')}
                </Label>
                <DurationInput
                  id="entry-on-call-hours"
                  label={t('timesheet.entry.onCallHours', 'Hours worked while on call')}
                  value={onCallHours}
                  onChange={setOnCallHours}
                />
                <p className="text-xs text-muted-foreground">
                  {t('timesheet.entry.onCallHoursHint', 'Leave 0:00 if there were no call-outs.')}
                </p>
              </div>
            )}
          </div>

          {/* Recap */}
          <div
            data-testid="entry-recap"
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md bg-muted/50 px-3 py-2 text-sm"
          >
            <span className="text-muted-foreground">
              {[
                t('timesheet.entry.recapOrdinary', '{{hours}} ordinary', { hours: formatDuration(workedHours) }),
                effectiveOvertimeHours > 0 &&
                  t('timesheet.entry.recapOvertime', '{{hours}} overtime', { hours: formatDuration(effectiveOvertimeHours) }),
                onCall &&
                  t('timesheet.entry.recapOnCall', 'on call ({{hours}})', { hours: formatDuration(effectiveOnCallHours) }),
              ].filter(Boolean).join(' · ')}
            </span>
            <span className="font-semibold tabular-nums">
              {t('timesheet.entry.recapTotal', 'Total {{hours}}', { hours: formatDuration(entryTotalHours) })}
            </span>
          </div>

          {nothingToLog && (
            <p className="text-xs text-muted-foreground">
              {t('timesheet.entry.nothingToLog', 'Enter some hours or mark the day as on call.')}
            </p>
          )}
    </div>
  );

  const actions = (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        {onDelete && mode === 'edit' && (!entryState || entryState === 'draft') && (
          <Button
            variant="outline"
            onClick={async () => {
              setDeleting(true);
              try {
                await onDelete();
              } finally {
                setDeleting(false);
              }
            }}
            disabled={saving || deleting}
            className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 gap-2 sm:w-auto"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {deleting ? t('timesheet.actions.deleting', 'Deleting...') : t('timesheet.actions.delete', 'Delete')}
          </Button>
        )}
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row">
        <Button variant="outline" onClick={handleClose} disabled={saving || deleting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || deleting || datesToFill.length === 0 || !selectedOrderId || !canSubmitHours}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="add-entry-submit"
        >
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {mode === 'edit'
            ? t('timesheet.saveEntry', 'Save Entry')
            : isRange
              ? t('timesheet.addEntries', 'Add {{count}} Entries', { count: datesToFill.length })
              : t('timesheet.addEntry', 'Add Entry')}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={handleClose}
        title={dialogTitle}
        description={dialogDescription}
      >
        {formBody}
        {actions}
      </BottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isRange ? (
              <CalendarRange className="h-5 w-5 text-orange-500" />
            ) : (
              <Calendar className="h-5 w-5 text-orange-500" />
            )}
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {formBody}
        {actions}
      </DialogContent>
    </Dialog>
  );
};

export default DateRangeEntryDialog;
